/**
 * 用戶 UsersTab (S10.1) — activate/deactivate (reason-gated), role assignment,
 * and「以此身分檢視權限」which switches the dev session to a user's identity so
 * ProtectedRoute's deny path can be previewed live (the narrow Sales user hits
 * 403 on 訂單控管中心). The prior administrator session is backed up and can be
 * restored from the tab header.
 *
 * S1.5 (rbacV2) — view-as is the DEV IDENTITY-SWITCH entry ("the 5th bypass
 * site") and writes a STRUCTURED identity, never a frozen flat permission list:
 *   • Seeded login identities go through `devSwitchIdentity` — a REAL
 *     `/auth/login` round-trip, the only client-reachable MSW-principal setter
 *     — so the stored profile carries the resolver-stamped `roleIds` +
 *     `homeOrgId` + `assignments` + `permissions[]` + `visibleOrgIds[]`, and
 *     the impersonated session proves BOTH axes: Axis A (403 on a missing
 *     grant, e.g. `occ.view`) and Axis B (scoped list handlers narrow to the
 *     principal's `visibleOrgIds`).
 *   • Any other governance user falls back to a client-side stamp through the
 *     SAME pure resolver over the live catalogs (the sanctioned client-side
 *     consumer — plan §Resolver contract, portability split), so Axis A and
 *     the profile-side Axis-B scope still hold; only the MSW-side session
 *     principal keeps its own identity (no dev impersonation endpoint yet).
 * The whole affordance (row entry, hint, restore) is `import.meta.env.DEV`-
 * gated and tree-shakes out of a production artifact — in production the
 * least-privilege PREVIEW surface (S3.1) is the sanctioned inspection tool.
 *
 * S2.4 (rbacV2) — scoped assignment:「設定組織範圍 Set org scope」opens
 * `OrgScopeAssignmentModal` (per assigned role: org anchor via the SAME shared
 * `TaxonomyTreeEditor`, mode select, read-only reach meter). The write is
 * reason-gated and lands `assignments[]` through the governance PATCH, which
 * reprojects the user's effective snapshot — the next login/`/auth/me` restamp
 * then carries the updated `visibleOrgIds` (e.g. re-scoping Sales@org-au
 * changes what Melissa's scoped lists show). The entry only renders under
 * `governance.rbacV2` and only for users who hold at least one role
 * (hide-don't-disable).
 *
 * S3.1 (rbacV2) — the user-detail effective-permission PREVIEW:
 *「檢視有效權限」opens a read-only Drawer mounting `EffectivePermissionPanel`
 * ("what can this user do, and why") — grants by human label with provenance
 * tags + the Axis-B reach, resolved live through the SAME pure resolver the
 * `/auth/me` stamp uses (parity asserted by the S3.1 vitest). This is the
 * production-sanctioned inspection surface; the DEV-only view-as switch above
 * PAIRS with it (lived experience) but never replaces it.
 */

import { useEffect, useState } from 'react';
import { App, Button, Drawer, Dropdown, Select, Space, Table, Tag, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ItemType } from 'antd/es/menu/interface';
import { MoreOutlined, RollbackOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearCache } from 'ahooks';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import StatusTag from '@/components/common/StatusTag';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import apiClient, { getUserProfile, setUserProfile } from '@/services/api/axios.config';
import { devSwitchIdentity } from '@/utils/devAuth';
import { fromProfile, resolveEffectiveAccess, type RoleAssignment } from '@/lib/rbac';
import { useFeature } from '@/features/flags';
import type { User } from '@/models/user';
import { useOrganisations, useRoles, useUsers, useSystemMutations } from './useSystemGovernance';
import OrgScopeAssignmentModal from './OrgScopeAssignmentModal';
import EffectivePermissionPanel from './EffectivePermissionPanel';
import type { GovernanceUser, GovernanceUserStatus } from './model';

const { Text } = Typography;

const VIEW_AS_BACKUP_KEY = 'userProfile.viewAsBackup';

const statusToTag = (status: GovernanceUserStatus): 'active' | 'inactive' | 'pending' | 'error' => {
  if (status === 'active') return 'active';
  if (status === 'invited') return 'pending';
  return 'error';
};

const UsersTab = () => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { users, loading, refresh } = useUsers();
  const { roles } = useRoles();
  const { organisations } = useOrganisations();
  const { setUserStatus, setUserRoles } = useSystemMutations();

  const rbacV2 = useFeature('governance.rbacV2');

  const [statusTarget, setStatusTarget] = useState<GovernanceUser | null>(null);
  const [roleTarget, setRoleTarget] = useState<GovernanceUser | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [scopeTarget, setScopeTarget] = useState<GovernanceUser | null>(null);
  const [previewTarget, setPreviewTarget] = useState<GovernanceUser | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    // DEV-fold (S1.5 production-artifact rule): the backup key exists only for
    // the dev identity switch, and this was the one surviving reference in a
    // production bundle — the folded guard lets the minifier drop the key
    // literal and the localStorage read entirely (grep-verified on dist/).
    if (import.meta.env.DEV) {
      setImpersonating(Boolean(localStorage.getItem(VIEW_AS_BACKUP_KEY)));
    }
  }, []);

  const submitStatus = async (reason: string) => {
    if (!statusTarget) return;
    const next: GovernanceUserStatus = statusTarget.status === 'active' ? 'disabled' : 'active';
    await setUserStatus(statusTarget.id, next, reason);
    message.success(t('messages.statusUpdated'));
    setStatusTarget(null);
    refresh();
  };

  const openRoleAssign = (user: GovernanceUser) => {
    setSelectedRoleIds([...user.roleIds]);
    setRoleTarget(user);
  };

  const submitRoles = async (reason: string) => {
    if (!roleTarget) return;
    await setUserRoles(roleTarget.id, selectedRoleIds, reason);
    message.success(t('messages.rolesUpdated'));
    setRoleTarget(null);
    refresh();
  };

  // S2.4 — slice-local assignments mutation. `useSystemGovernance` (owned by
  // S2.2 this wave) does not expose an assignments write yet, so the PATCH +
  // cache invalidation mirror `setUserRoles` here; follow-up recorded to fold
  // this into `useSystemMutations` once its owner frees the file.
  const submitAssignments = async (assignments: RoleAssignment[], reason: string) => {
    if (!scopeTarget) return;
    await apiClient.patch(`/governance/users/${scopeTarget.id}`, {
      assignments,
      reason,
      actorName: (getUserProfile() as User | null)?.name ?? undefined,
    });
    clearCache('/api/governance/users');
    clearCache('/api/governance/audit-logs');
    message.success(t('messages.assignmentsUpdated'));
    setScopeTarget(null);
    refresh();
  };

  const viewAs = async (user: GovernanceUser) => {
    const current = getUserProfile();
    if (current && !localStorage.getItem(VIEW_AS_BACKUP_KEY)) {
      localStorage.setItem(VIEW_AS_BACKUP_KEY, JSON.stringify(current));
    }

    // Preferred path — a real login round-trip (dev identity switch): flips the
    // MSW session principal so the Axis-B scoped list handlers see the
    // impersonated principal, and stores the freshly resolver-STAMPED
    // structured profile (`roleIds` + `homeOrgId` + `assignments` +
    // `permissions[]` + `visibleOrgIds[]`). Returns null when `user` is not a
    // seeded login identity (or outside DEV).
    const switched = await devSwitchIdentity(user.email, user.id);

    if (!switched) {
      // Fallback — client-side preview stamp: build the STRUCTURED identity
      // from the governance record and resolve it through the very same pure
      // resolver over the live catalogs (shared-selector discipline; parity-
      // tested in S0.4). The route guard (Axis A) and `canInScope` (Axis B
      // over the stamped `visibleOrgIds`) behave exactly as with a server
      // stamp; only the MSW-side session principal keeps its own identity.
      const access = resolveEffectiveAccess({
        identity: fromProfile(user),
        roles: roles ?? [],
        orgs: organisations ?? [],
      });
      setUserProfile({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.roleIds.includes('role-admin') ? 'admin' : 'staff',
        roleIds: [...user.roleIds],
        homeOrgId: user.homeOrgId,
        assignments: user.assignments ? [...user.assignments] : [],
        permissions: [...access.grants],
        visibleOrgIds: [...access.visibleOrgIds],
        avatar: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }

    setImpersonating(true);
    message.info(t('users.viewingAs', { name: user.name }));
    navigate('/auth/occ');
  };

  const restoreAdmin = async () => {
    const raw = localStorage.getItem(VIEW_AS_BACKUP_KEY);
    if (raw) {
      try {
        const backup = JSON.parse(raw) as User;
        // Real round-trip: resets the MSW session principal to the backed-up
        // administrator AND restamps the profile fresh over the live catalogs
        // (Open Q8 snapshot semantics — never restore a stale frozen stamp
        // when a live one is mintable). Falls back to the raw backup if the
        // switch is unavailable.
        const restored = await devSwitchIdentity(backup.email, backup.id);
        if (!restored) {
          setUserProfile(backup);
        }
      } catch {
        /* corrupt backup — leave the current session untouched */
      }
      localStorage.removeItem(VIEW_AS_BACKUP_KEY);
    }
    setImpersonating(false);
    message.success(t('users.restored'));
    navigate('/auth/governance/users');
  };

  const columns: ColumnsType<GovernanceUser> = [
    { title: t('fields.name'), dataIndex: 'name', render: (v: string) => <Text strong>{v}</Text> },
    { title: t('fields.email'), dataIndex: 'email', width: 220 },
    { title: t('fields.organisation'), dataIndex: 'organisationName', width: 200 },
    {
      title: t('fields.roles'),
      dataIndex: 'roleNames',
      render: (roleNames: string[]) =>
        roleNames.length ? (
          <Space wrap>
            {roleNames.map((name) => (
              <Tag key={name} color="purple">
                {name}
              </Tag>
            ))}
          </Space>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('fields.status'),
      dataIndex: 'status',
      width: 120,
      render: (status: GovernanceUserStatus) => (
        <StatusTag status={statusToTag(status)} label={t(`status.${status}`)} />
      ),
    },
    { title: t('fields.lastLogin'), dataIndex: 'lastLoginAt', width: 170, render: renderDash },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'assign', label: t('actions.assignRoles'), onClick: () => openRoleAssign(record) },
              // S3.1 — the effective-permission preview rides the rbacV2 flag.
              // Rendered for EVERY user (a role-less user resolves honestly to
              // zero grants — the panel explains that, so nothing to hide).
              ...(rbacV2
                ? ([
                    {
                      key: 'effective',
                      label: t('actions.viewEffective'),
                      onClick: () => setPreviewTarget(record),
                    },
                  ] satisfies ItemType[])
                : []),
              // S2.4 — org-scope anchoring rides the rbacV2 flag; a user with
              // no roles has nothing to anchor, so the entry is HIDDEN for
              // them (hide-don't-disable), not greyed.
              ...(rbacV2 && record.roleIds.length > 0
                ? ([
                    {
                      key: 'orgScope',
                      label: t('actions.setOrgScope'),
                      onClick: () => setScopeTarget(record),
                    },
                  ] satisfies ItemType[])
                : []),
              {
                key: 'toggle',
                label: record.status === 'active' ? t('actions.deactivate') : t('actions.activate'),
                danger: record.status === 'active',
                style: record.status !== 'active' ? { color: token.colorSuccess } : undefined,
                onClick: () => setStatusTarget(record),
              },
              // The dev identity-switch entry (S1.5) — `import.meta.env.DEV`
              // is folded at build time, so this spread is `[]` in a
              // production artifact and the switcher tree-shakes out.
              ...(import.meta.env.DEV
                ? ([
                    { type: 'divider' },
                    {
                      key: 'viewAs',
                      label: t('actions.viewAs'),
                      onClick: () => {
                        void viewAs(record);
                      },
                    },
                  ] satisfies ItemType[])
                : []),
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* Dev-only impersonation header (S1.5) — hint + restore ride the same
          `import.meta.env.DEV` fold as the row entry; a production artifact
          renders neither. */}
      {import.meta.env.DEV && (
        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
          <Text type="secondary">{t('users.viewAsHint')}</Text>
          {impersonating && (
            <Button
              size="large"
              icon={<RollbackOutlined />}
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => {
                void restoreAdmin();
              }}
            >
              {t('actions.restoreAdmin')}
            </Button>
          )}
        </div>
      )}

      <TableContainer>
        <Table<GovernanceUser>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={users ?? []}
          rowClassName={(record) => (record.status === 'disabled' ? 'inactive-row' : '')}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => String(total) }}
          scroll={{ x: 1040 }}
          locale={{ emptyText: <ListEmptyState title={t('empty.users')} /> }}
        />
      </TableContainer>

      <ReasonRequiredModal
        open={statusTarget !== null}
        title={
          statusTarget?.status === 'active'
            ? t('reasonModal.deactivateUserTitle')
            : t('reasonModal.activateUserTitle')
        }
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        description={statusTarget ? `${statusTarget.name} · ${statusTarget.email}` : undefined}
        onSubmit={submitStatus}
        onCancel={() => setStatusTarget(null)}
      />

      <ReasonRequiredModal
        open={roleTarget !== null}
        title={t('users.assignTitle')}
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        description={roleTarget?.name}
        onSubmit={submitRoles}
        onCancel={() => setRoleTarget(null)}
      >
        <div style={{ marginBottom: token.marginMD }}>
          <Text style={formStyles.labelStyle}>{t('fields.roles')}</Text>
          <Select
            mode="multiple"
            size="large"
            style={{ width: '100%' }}
            value={selectedRoleIds}
            onChange={setSelectedRoleIds}
            options={(roles ?? []).map((role) => ({ label: role.name, value: role.id }))}
          />
        </div>
      </ReasonRequiredModal>

      {/* S2.4 — per-role org anchor + mode + read-only reach meter (rbacV2). */}
      {rbacV2 && (
        <OrgScopeAssignmentModal
          open={scopeTarget !== null}
          user={scopeTarget}
          roles={roles ?? []}
          organisations={organisations ?? []}
          onSubmit={submitAssignments}
          onCancel={() => setScopeTarget(null)}
        />
      )}

      {/* S3.1 — read-only effective-permission preview drawer (rbacV2). */}
      {rbacV2 && (
        <Drawer
          title={t('effective.title')}
          width={560}
          open={previewTarget !== null}
          onClose={() => setPreviewTarget(null)}
        >
          {previewTarget && (
            <EffectivePermissionPanel
              user={previewTarget}
              roles={roles ?? []}
              organisations={organisations ?? []}
            />
          )}
        </Drawer>
      )}
    </div>
  );
};

export default UsersTab;
